import { Helmet } from "react-helmet-async";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import api from "../Utils/Axios";
import DashboardHeader from "./CompanyComponents/DashboardHeaderRouter";

function ExpenseFieldsDetails() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const [isUrdu, setIsUrdu] = useState(false);
  const printRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("companyToken");
    localStorage.removeItem("companyData");
    toast.success(companyLanguage === 'ur' ? "کامیابی سے لاگ آؤٹ ہو گئے" : "Logged out successfully");
    navigate("/company/login");
  };

  // Expense fields data - each field will have its own rows
  const [expenseFieldsData, setExpenseFieldsData] = useState({
    cash: [], // نقدی
    rent: [], // کرایه
    commission: [], // کمیشن
    labor: [], // مزدوری
    mashiana: [], // مشیانه
    marketFees: [] // مارکیٹ فیس
  });

  const [loading, setLoading] = useState(false);
  const [bills, setBills] = useState([]);
  const [recoveries, setRecoveries] = useState([]);
  const [ledgerParties, setLedgerParties] = useState([]);
  const [activeTab, setActiveTab] = useState('cash'); // Default to cash tab

  // Daily Kharcha state
  const [dailyKharchas, setDailyKharchas] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [dailyKharchaForm, setDailyKharchaForm] = useState({
    date: new Date().toLocaleDateString('en-CA'),
    amount: '',
    tafseel: '',
    tafseelUrdu: '',
    paymentMethod: ''
  });
  const [editingDailyKharcha, setEditingDailyKharcha] = useState(null);
  const [isSavingDailyKharcha, setIsSavingDailyKharcha] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterAmountMin, setFilterAmountMin] = useState('');
  const [filterAmountMax, setFilterAmountMax] = useState('');
  const [filterBalanceMin, setFilterBalanceMin] = useState('');
  const [filterBalanceMax, setFilterBalanceMax] = useState('');
  const [showFilters, setShowFilters] = useState(true); // Auto-open filters by default

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const token = localStorage.getItem("companyToken");
        if (!token) {
          navigate("/company-login");
          return;
        }

        const response = await api.get("/company/profile", {
          headers: { Authorization: `Bearer ${token}` }
        });

        setCompany(response.data.company);
        setCompanyLanguage(response.data.company?.language || 'en');
        setIsUrdu(response.data.company?.language === 'ur');
      } catch (error) {
        console.error("Error fetching company:", error);
        navigate("/company-login");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompany();
  }, [navigate]);

  useEffect(() => {
    if (company) {
      fetchData();
      fetchDailyKharchas();
      fetchPaymentMethods();
    }
  }, [company]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get("/payment-method/active");
      const methods = response.data.paymentMethods || [];
      setPaymentMethods(methods);
    } catch (error) {
      console.error("Fetch payment methods error:", error);
      setPaymentMethods([]);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("companyToken");

      if (!token) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم لاگ ان کریں' : 'Please login');
        return;
      }

      // Fetch bills
      const billsResponse = await api.get("/bill/company/my-bills", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const billsData = billsResponse.data.bills || [];
      setBills(billsData);

      // Fetch ledger parties for supplier names
      const partiesResponse = await api.get("/ledger/parties", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const parties = partiesResponse.data.parties || [];
      setLedgerParties(parties);

      // Fetch recoveries
      let recoveriesData = [];
      try {
        const recoveriesResponse = await api.get("/recovery/company/all", {
          headers: { Authorization: `Bearer ${token}` }
        });
        recoveriesData = recoveriesResponse.data.recoveries || [];
        setRecoveries(recoveriesData);
      } catch (error) {
        console.error("Error fetching recoveries:", error);
        setRecoveries([]);
      }

      // Process bills and organize by expense fields
      const organizedData = {
        cash: [],
        rent: [],
        commission: [],
        labor: [],
        mashiana: [],
        marketFees: []
      };

      // Create separate recovery maps for each expense field type
      // Format: recoveryMaps[fieldType][billNumber] = amount
      const recoveryMaps = {
        cash: new Map(),
        rent: new Map(),
        commission: new Map(),
        labor: new Map(),
        mashiana: new Map(),
        marketFees: new Map()
      };

      // Field name mapping for matching
      const fieldNameMapping = {
        'Cash': 'cash',
        'Rent': 'rent',
        'Commission': 'commission',
        'Labor': 'labor',
        'Mashiana': 'mashiana',
        'Miscellaneous': 'mashiana',
        'Market Fees': 'marketFees',
        'Market Fee': 'marketFees',
        'نقدی': 'cash',
        'کرایه': 'rent',
        'کمیشن': 'commission',
        'مزدوری': 'labor',
        'مشیانه': 'mashiana',
        'مارکیٹ فیس': 'marketFees'
      };

      recoveriesData.forEach(recovery => {
        const recoveryAmount = parseFloat(recovery.recoveryAmount || 0);

        if (recoveryAmount > 0) {
          let fieldType = null;
          let billNumber = null;

          // Extract field type and bill number from description
          if (recovery.description) {
            // Check for EXPENSE_FIELD format: EXPENSE_FIELD:FieldName:BillNumber
            const expenseFieldMatch = recovery.description.match(/EXPENSE_FIELD:([^:]+):(.+)/);
            if (expenseFieldMatch) {
              const fieldName = expenseFieldMatch[1].trim();
              billNumber = expenseFieldMatch[2].trim();
              fieldType = fieldNameMapping[fieldName] || null;
            } else {
              // Fallback: Try to extract from description text
              for (const [key, value] of Object.entries(fieldNameMapping)) {
                if (recovery.description.includes(key)) {
                  fieldType = value;
                  break;
                }
              }

              // Try to extract bill number from description
              const billMatch = recovery.description.match(/Bill\s+(\w+)/i) ||
                recovery.description.match(/بل\s+(\w+)/i) ||
                recovery.description.match(/(\d+)/);
              if (billMatch) {
                billNumber = billMatch[1];
              }
            }
          }

          // If billId is available, get bill number from bill
          if (!billNumber && recovery.billId) {
            const matchingBill = billsData.find(b => {
              const bId = b._id?.toString();
              return bId === recovery.billId?.toString();
            });
            if (matchingBill) {
              billNumber = matchingBill.billNumber || matchingBill.billNo || matchingBill.serialNumber || '';
            }
          }

          // Store recovery by field type and bill number
          if (fieldType && billNumber && recoveryMaps[fieldType]) {
            const key = billNumber.toString();
            if (!recoveryMaps[fieldType].has(key)) {
              recoveryMaps[fieldType].set(key, 0);
            }
            recoveryMaps[fieldType].set(key, recoveryMaps[fieldType].get(key) + recoveryAmount);
          }
        }
      });

      billsData.forEach(bill => {
        const billDate = bill.date ? new Date(bill.date).toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA');
        const billNumber = bill.billNumber || bill.billNo || bill.serialNumber || bill._id?.toString().slice(-6) || '';
        const supplierParty = parties.find(p => p._id?.toString() === bill.customerPartyId?.toString());
        const supplierName = isUrdu ? (supplierParty?.nameUrdu || supplierParty?.name) : (supplierParty?.name || supplierParty?.nameUrdu) || bill.customerName || '';

        // Get actual sale value from bill
        const sale = parseFloat(bill.netSale || bill.sale || bill.totalSale || bill.grossSale || 0);

        const expenses = bill.expenses || {};

        // Cash (نقدی)
        const cashValue = parseFloat(expenses.Cash || expenses.نقدی || 0);
        if (cashValue > 0) {
          const nakadi = cashValue; // Actual cash value from this bill
          // Get recovery amount for this specific field and bill number
          const cashRecovery = recoveryMaps.cash.get(billNumber.toString()) || 0;
          // Balance = Field Value - Recovery
          const balance = cashValue - cashRecovery;
          organizedData.cash.push({
            date: billDate,
            billNumber: billNumber,
            supplierName: supplierName,
            sale: sale,
            nakadi: nakadi,
            balance: balance,
            billId: bill._id,
            amount: cashValue,
            recovery: cashRecovery
          });
        }

        // Rent (کرایه)
        const rentValue = parseFloat(expenses.Rent || expenses.کرایه || expenses.Kiraya || 0);
        if (rentValue > 0) {
          const nakadi = rentValue; // Actual rent value from this bill
          // Get recovery amount for this specific field and bill number
          const rentRecovery = recoveryMaps.rent.get(billNumber.toString()) || 0;
          // Balance = Field Value - Recovery
          const balance = rentValue - rentRecovery;
          organizedData.rent.push({
            date: billDate,
            billNumber: billNumber,
            supplierName: supplierName,
            sale: sale,
            nakadi: nakadi,
            balance: balance,
            billId: bill._id,
            amount: rentValue,
            recovery: rentRecovery
          });
        }

        // Commission (کمیشن)
        const commissionValue = parseFloat(expenses.Commission || expenses.کمیشن || 0);
        if (commissionValue > 0) {
          const nakadi = commissionValue; // Actual commission value from this bill
          // Get recovery amount for this specific field and bill number
          const commissionRecovery = recoveryMaps.commission.get(billNumber.toString()) || 0;
          // Balance = Field Value - Recovery
          const balance = commissionValue - commissionRecovery;
          organizedData.commission.push({
            date: billDate,
            billNumber: billNumber,
            supplierName: supplierName,
            sale: sale,
            nakadi: nakadi,
            balance: balance,
            billId: bill._id,
            amount: commissionValue,
            recovery: commissionRecovery
          });
        }

        // Labor (مزدوری)
        const laborValue = parseFloat(expenses.Labor || expenses.Wages || expenses.مزدوری || expenses.Mazdoori || 0);
        if (laborValue > 0) {
          const nakadi = laborValue; // Actual labor value from this bill
          // Get recovery amount for this specific field and bill number
          const laborRecovery = recoveryMaps.labor.get(billNumber.toString()) || 0;
          // Balance = Field Value - Recovery
          const balance = laborValue - laborRecovery;
          organizedData.labor.push({
            date: billDate,
            billNumber: billNumber,
            supplierName: supplierName,
            sale: sale,
            nakadi: nakadi,
            balance: balance,
            billId: bill._id,
            amount: laborValue,
            recovery: laborRecovery
          });
        }

        // Mashiana (مشیانه)
        const mashianaValue = parseFloat(
          expenses.Miscellaneous ||
          expenses.Mashiyana ||
          expenses.مشیانہ ||
          expenses.مشیانه ||
          expenses.Mashiana ||
          0
        );
        if (mashianaValue > 0) {
          const nakadi = mashianaValue; // Actual mashiana value from this bill
          // Get recovery amount for this specific field and bill number
          const mashianaRecovery = recoveryMaps.mashiana.get(billNumber.toString()) || 0;
          // Balance = Field Value - Recovery
          const balance = mashianaValue - mashianaRecovery;
          organizedData.mashiana.push({
            date: billDate,
            billNumber: billNumber,
            supplierName: supplierName,
            sale: sale,
            nakadi: nakadi,
            balance: balance,
            billId: bill._id,
            amount: mashianaValue,
            recovery: mashianaRecovery
          });
        }

        // Market Fees (مارکیٹ فیس)
        const marketFeesValue = parseFloat(
          expenses['Market Fees'] ||
          expenses['Market Fee'] ||
          expenses.مارکیٹ_فیس ||
          expenses.مارکیٹ_فیس ||
          expenses.MarketFees ||
          0
        );
        if (marketFeesValue > 0) {
          const nakadi = marketFeesValue; // Actual market fees value from this bill
          // Get recovery amount for this specific field and bill number
          const marketFeesRecovery = recoveryMaps.marketFees.get(billNumber.toString()) || 0;
          // Balance = Field Value - Recovery
          const balance = marketFeesValue - marketFeesRecovery;
          organizedData.marketFees.push({
            date: billDate,
            billNumber: billNumber,
            supplierName: supplierName,
            sale: sale,
            nakadi: nakadi,
            balance: balance,
            billId: bill._id,
            amount: marketFeesValue,
            recovery: marketFeesRecovery
          });
        }
      });

      // Sort each field's data by bill number (numeric) then by date
      const sortByBillSequence = (a, b) => {
        // Extract numeric part from bill number
        const getBillNumber = (billNum) => {
          if (!billNum) return 0;
          const numMatch = billNum.toString().match(/\d+/);
          return numMatch ? parseInt(numMatch[0], 10) : 0;
        };

        const billNumA = getBillNumber(a.billNumber);
        const billNumB = getBillNumber(b.billNumber);

        // First sort by bill number (numeric)
        if (billNumA !== billNumB) {
          return billNumA - billNumB;
        }

        // If bill numbers are same, sort by date
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return dateA - dateB;
      };

      // Sort all fields
      organizedData.cash.sort(sortByBillSequence);
      organizedData.rent.sort(sortByBillSequence);
      organizedData.commission.sort(sortByBillSequence);
      organizedData.labor.sort(sortByBillSequence);
      organizedData.mashiana.sort(sortByBillSequence);
      organizedData.marketFees.sort(sortByBillSequence);

      // Calculate running balance for each field
      const calculateRunningBalance = (data) => {
        let runningBalance = 0;
        return data.map(row => {
          // Current row balance = Field Value - Recovery
          const currentBalance = row.balance || 0;
          // Add to running balance
          runningBalance += currentBalance;
          return {
            ...row,
            balance: runningBalance
          };
        });
      };

      // Apply running balance to all fields
      organizedData.cash = calculateRunningBalance(organizedData.cash);
      organizedData.rent = calculateRunningBalance(organizedData.rent);
      organizedData.commission = calculateRunningBalance(organizedData.commission);
      organizedData.labor = calculateRunningBalance(organizedData.labor);
      organizedData.mashiana = calculateRunningBalance(organizedData.mashiana);
      organizedData.marketFees = calculateRunningBalance(organizedData.marketFees);

      setExpenseFieldsData(organizedData);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(companyLanguage === 'ur' ? 'ڈیٹا لوڈ نہیں ہو سکا' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyKharchas = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      const response = await api.get("/daily-kharcha/company/all", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyKharchas(response.data.dailyKharchas || []);
    } catch (error) {
      console.error("Fetch daily kharchas error:", error);
    }
  };

  const handleDailyKharchaSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSavingDailyKharcha(true);
      const token = localStorage.getItem("companyToken");

      const payload = {
        date: dailyKharchaForm.date,
        amount: parseFloat(dailyKharchaForm.amount) || 0,
        tafseel: dailyKharchaForm.tafseel,
        tafseelUrdu: dailyKharchaForm.tafseelUrdu
      };

      if (editingDailyKharcha) {
        await api.put(`/daily-kharcha/company/${editingDailyKharcha._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(isUrdu ? 'روزانه خرچہ اپ ڈیٹ ہو گیا' : 'Daily kharcha updated successfully');
      } else {
        await api.post("/daily-kharcha/company/create", payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(isUrdu ? 'روزانه خرچہ شامل ہو گیا' : 'Daily kharcha added successfully');
      }

      setDailyKharchaForm({
        date: new Date().toLocaleDateString('en-CA'),
        amount: '',
        tafseel: '',
        tafseelUrdu: '',
        paymentMethod: ''
      });
      setEditingDailyKharcha(null);
      fetchDailyKharchas();
    } catch (error) {
      console.error("Save daily kharcha error:", error);
      toast.error(isUrdu ? 'روزانه خرچہ محفوظ کرنے میں ناکام' : "Failed to save daily kharcha");
    } finally {
      setIsSavingDailyKharcha(false);
    }
  };

  const handleDeleteDailyKharcha = async (id) => {
    if (!window.confirm(isUrdu ? 'کیا آپ واقعی یہ روزانه خرچہ حذف کرنا چاہتے ہیں؟' : 'Are you sure you want to delete this daily kharcha?')) {
      return;
    }

    try {
      const token = localStorage.getItem("companyToken");
      await api.delete(`/daily-kharcha/company/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isUrdu ? 'روزانه خرچہ حذف ہو گیا' : 'Daily kharcha deleted successfully');
      fetchDailyKharchas();
    } catch (error) {
      console.error("Delete daily kharcha error:", error);
      toast.error(isUrdu ? 'روزانه خرچہ حذف کرنے میں ناکام' : "Failed to delete daily kharcha");
    }
  };

  const handleEditDailyKharcha = (dailyKharcha) => {
    setEditingDailyKharcha(dailyKharcha);
    setDailyKharchaForm({
      date: dailyKharcha.date ? new Date(dailyKharcha.date).toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA'),
      amount: dailyKharcha.amount || '',
      tafseel: dailyKharcha.tafseel || '',
      tafseelUrdu: dailyKharcha.tafseelUrdu || '',
      paymentMethod: dailyKharcha.paymentMethod || ''
    });
  };

  // Get unique suppliers for filter dropdown
  const uniqueSuppliers = useMemo(() => {
    const suppliers = new Set();
    Object.values(expenseFieldsData).forEach(fieldData => {
      fieldData.forEach(row => {
        if (row.supplierName) suppliers.add(row.supplierName);
      });
    });
    return Array.from(suppliers).sort();
  }, [expenseFieldsData]);

  // Filter function for expense field data
  const filterExpenseData = useCallback((data) => {
    return data.filter(row => {
      // Search term filter (searches in bill number, supplier name)
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const billNumber = (row.billNumber || '').toString().toLowerCase();
        const supplierName = (row.supplierName || '').toLowerCase();

        if (!billNumber.includes(searchLower) && !supplierName.includes(searchLower)) {
          return false;
        }
      }

      // Date range filter
      if (filterDateFrom) {
        const rowDate = new Date(row.date);
        const fromDate = new Date(filterDateFrom);
        if (rowDate < fromDate) return false;
      }
      if (filterDateTo) {
        const rowDate = new Date(row.date);
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999); // Include entire day
        if (rowDate > toDate) return false;
      }

      // Supplier filter
      if (filterSupplier) {
        if (row.supplierName !== filterSupplier) return false;
      }

      // Amount range filter
      const amount = row.amount || row.nakadi || 0;
      if (filterAmountMin) {
        if (amount < parseFloat(filterAmountMin)) return false;
      }
      if (filterAmountMax) {
        if (amount > parseFloat(filterAmountMax)) return false;
      }

      // Balance range filter
      const balance = row.balance || 0;
      if (filterBalanceMin) {
        if (balance < parseFloat(filterBalanceMin)) return false;
      }
      if (filterBalanceMax) {
        if (balance > parseFloat(filterBalanceMax)) return false;
      }

      return true;
    });
  }, [searchTerm, filterDateFrom, filterDateTo, filterSupplier, filterAmountMin, filterAmountMax, filterBalanceMin, filterBalanceMax]);

  // Filtered data for each expense field
  const filteredExpenseFieldsData = useMemo(() => {
    return {
      cash: filterExpenseData(expenseFieldsData.cash),
      rent: filterExpenseData(expenseFieldsData.rent),
      commission: filterExpenseData(expenseFieldsData.commission),
      labor: filterExpenseData(expenseFieldsData.labor),
      mashiana: filterExpenseData(expenseFieldsData.mashiana),
      marketFees: filterExpenseData(expenseFieldsData.marketFees)
    };
  }, [expenseFieldsData, filterExpenseData]);

  const tabMeta = useMemo(() => ({
    cash: { en: 'Cash', ur: 'نقدی' },
    rent: { en: 'Rent', ur: 'کرایه' },
    commission: { en: 'Commission', ur: 'کمیشن' },
    labor: { en: 'Labor', ur: 'مزدوری' },
    mashiana: { en: 'Mashiana', ur: 'مشیانه' },
    marketFees: { en: 'Market Fees', ur: 'مارکیٹ فیس' },
    dailyKharcha: { en: 'Daily Kharcha', ur: 'روزانه خرچہ' },
  }), []);

  const activeTabTitle = isUrdu ? (tabMeta[activeTab]?.ur || '') : (tabMeta[activeTab]?.en || '');
  const activeTabData = filteredExpenseFieldsData?.[activeTab] || [];

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadExcel = () => {
    try {
      const header = [
        isUrdu ? 'تاریخ' : 'Date',
        isUrdu ? 'بل نمبر' : 'Bill #',
        isUrdu ? 'سپلائر' : 'Supplier',
        isUrdu ? 'رقم' : 'Amount',
        isUrdu ? 'ریکوری' : 'Recovery',
        isUrdu ? 'بقایا' : 'Balance',
      ];

      const rows = activeTabData.map((row) => ([
        row?.date ? new Date(row.date).toLocaleDateString() : '',
        row?.billNumber || '',
        row?.supplierName || '',
        Number(row?.amount || 0),
        Number(row?.recovery || 0),
        Number(row?.balance || 0),
      ]));

      const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
      const wb = XLSX.utils.book_new();
      const sheetName = (activeTabTitle || 'Expense').slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      const fileName = `ExpenseFieldsDetails_${activeTab}_${new Date().toLocaleDateString('en-CA')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(isUrdu ? 'Excel فائل ڈاؤن لوڈ ہو گئی' : 'Excel downloaded');
    } catch (e) {
      console.error('Excel export error:', e);
      toast.error(isUrdu ? 'Excel بنانے میں ناکام' : 'Failed to export Excel');
    }
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) {
      toast.error(isUrdu ? 'مواد نہیں ملا' : 'Content not found');
      return;
    }

    try {
      setIsGeneratingPDF(true);
      toast.info(isUrdu ? 'PDF بنایا جا رہا ہے...' : 'Generating PDF...');

      const element = printRef.current;
      const hiddenEls = element.querySelectorAll('[data-export-hide="true"]');
      hiddenEls.forEach((el) => (el.style.display = 'none'));

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: Math.max(element.scrollWidth, element.clientWidth),
        windowHeight: Math.max(element.scrollHeight, element.clientHeight),
        onclone: (doc) => {
          // Ensure print-only CSS doesn't hide content in the cloned document
          const style = doc.createElement('style');
          style.textContent = `
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            body * { visibility: visible !important; }
            [data-export-hide="true"] { display: none !important; }
          `;
          doc.head.appendChild(style);

          const area = doc.getElementById('expense-details-print-area');
          if (area) {
            area.style.position = 'static';
            area.style.visibility = 'visible';
            area.style.maxHeight = 'none';
            area.style.overflow = 'visible';
          }

          // Remove overflow constraints so full tables render into canvas
          doc.querySelectorAll('#expense-details-print-area .overflow-auto, #expense-details-print-area .overflow-x-auto, #expense-details-print-area .overflow-y-auto')
            .forEach((el) => {
              el.style.overflow = 'visible';
              el.style.maxHeight = 'none';
              el.style.height = 'auto';
            });
        },
      });

      hiddenEls.forEach((el) => (el.style.display = ''));

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `ExpenseFieldsDetails_${activeTab}_${new Date().toLocaleDateString('en-CA')}.pdf`;
      pdf.save(fileName);
      toast.success(isUrdu ? 'PDF ڈاؤن لوڈ ہو گیا!' : 'PDF downloaded');
    } catch (e) {
      console.error('PDF export error:', e);
      toast.error(isUrdu ? 'PDF بنانے میں ناکام' : 'Failed to export PDF');
    } finally {
      // Always restore hidden elements (in case of error)
      try {
        const element = printRef.current;
        if (element) {
          element.querySelectorAll('[data-export-hide="true"]').forEach((el) => (el.style.display = ''));
        }
      } catch (err) {
        // ignore
      }
      setIsGeneratingPDF(false);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterSupplier('');
    setFilterAmountMin('');
    setFilterAmountMax('');
    setFilterBalanceMin('');
    setFilterBalanceMax('');
  };

  // Check if any filter is active
  const hasActiveFilters = searchTerm || filterDateFrom || filterDateTo || filterSupplier || filterAmountMin || filterAmountMax || filterBalanceMin || filterBalanceMax;

  const renderFieldTable = (fieldKey, fieldName, fieldNameUrdu, data) => {
    const displayName = isUrdu ? fieldNameUrdu : fieldName;

    return (
      <div key={fieldKey} className="mb-4" dir={isUrdu ? 'rtl' : 'ltr'}>
        <h3 className={`text-lg font-bold mb-2 text-purple-800 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
          {displayName}
        </h3>

        {data.length > 0 ? (
          <div className="overflow-auto max-h-[calc(100vh-360px)] rounded-lg border border-gray-300 bg-white shadow-sm" dir={isUrdu ? 'rtl' : 'ltr'}>
            <table className="min-w-full" dir={isUrdu ? 'rtl' : 'ltr'}>
              <thead className="bg-gradient-to-r from-purple-100 to-pink-100">
                <tr>
                  <th className={`px-4 py-3 text-xs font-bold text-gray-700 uppercase border-b ${isUrdu ? 'font-urdu text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                    {isUrdu ? 'تاریخ' : 'Date'}
                  </th>
                  <th className={`px-4 py-3 text-xs font-bold text-gray-700 uppercase border-b ${isUrdu ? 'font-urdu text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                    {isUrdu ? 'بل نمبر' : 'Bill Number'}
                  </th>
                  <th className={`px-4 py-3 text-xs font-bold text-gray-700 uppercase border-b ${isUrdu ? 'font-urdu text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                    {isUrdu ? 'سپلائر' : 'Supplier Name'}
                  </th>
                  <th className={`px-4 py-3 text-xs font-bold text-gray-700 uppercase border-b ${isUrdu ? 'font-urdu text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                    {displayName}
                  </th>
                  <th className={`px-4 py-3 text-xs font-bold text-gray-700 uppercase border-b ${isUrdu ? 'font-urdu text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                    {isUrdu ? 'ریکوری' : 'Recovery'}
                  </th>
                  <th className={`px-4 py-3 text-xs font-bold text-gray-700 uppercase border-b ${isUrdu ? 'font-urdu text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                    {isUrdu ? 'بقایا' : 'Balance'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className={`px-4 py-3 text-sm text-gray-800 ${isUrdu ? 'text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                      {row.date}
                    </td>
                    <td className={`px-4 py-3 text-sm font-semibold text-purple-700 ${isUrdu ? 'text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                      {row.billNumber}
                    </td>
                    <td className={`px-4 py-3 text-sm text-gray-800 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left', direction: isUrdu ? 'rtl' : 'ltr' }}>
                      {row.supplierName}
                    </td>
                    <td className={`px-4 py-3 text-sm text-gray-800 ${isUrdu ? 'text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                      {row.nakadi.toFixed(2)}
                    </td>
                    <td className={`px-4 py-3 text-sm text-gray-800 ${isUrdu ? 'text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                      {(row.recovery || 0).toFixed(2)}
                    </td>
                    <td className={`px-4 py-3 text-sm font-semibold ${row.balance >= 0 ? 'text-green-600' : 'text-red-600'} ${isUrdu ? 'text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                      {row.balance.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 print:hidden" data-export-hide="true">
                <tr>
                  <td colSpan="3" className={`px-4 py-3 text-sm font-bold text-gray-700 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                    {isUrdu ? 'کل' : 'Total'}
                  </td>
                  <td className={`px-4 py-3 text-sm font-bold text-gray-700 ${isUrdu ? 'text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                    {data.reduce((sum, row) => sum + (row.amount || 0), 0).toFixed(2)}
                  </td>
                  <td className={`px-4 py-3 text-sm font-bold text-gray-700 ${isUrdu ? 'text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                    {data.reduce((sum, row) => sum + (row.recovery || 0), 0).toFixed(2)}
                  </td>
                  <td className={`px-4 py-3 text-sm font-bold text-gray-700 ${isUrdu ? 'text-right' : 'text-left'}`} style={{ textAlign: isUrdu ? 'right' : 'left' }}>
                    {data.reduce((sum, row) => sum + (row.balance || 0), 0).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className={`text-center py-8 text-gray-500 ${isUrdu ? 'font-urdu' : ''}`} dir={isUrdu ? 'rtl' : 'ltr'}>
            {isUrdu ? 'کوئی ڈیٹا نہیں ملا' : 'No data found'}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className={`mt-4 text-gray-600 ${isUrdu ? 'font-urdu' : ''}`}>
            {isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isUrdu ? 'اخراجات کی تفصیلات' : 'Expense Fields Details'}</title>
      </Helmet>

      {/* Company Header - Fixed at Top */}
      {/* Company Header - Hidden */}
      {/* {company && (
        <DashboardHeader 
          company={company} 
          companyLanguage={companyLanguage} 
          onLogout={handleLogout} 
        />
      )} */}

      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-red-50 pt-[6px] pb-8 px-4" dir={isUrdu ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto">
          {/* Header with Dashboard Button */}
          <div className="bg-gradient-to-r from-white to-purple-50/30 rounded-xl shadow-xl border border-purple-100 mb-6 mt-4 overflow-hidden" dir={isUrdu ? 'rtl' : 'ltr'}>
            <div className={`flex justify-between items-center py-6 px-5 ${isUrdu ? 'flex-row-reverse' : ''}`} style={{ minHeight: '80px' }}>
              {/* Dashboard Button - Left side in Urdu, Right side in English */}
              <button
                onClick={() => navigate('/company/dashboard')}
                className={`group relative bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold px-6 py-3 rounded-xl transition-all transform hover:scale-105 hover:shadow-xl flex items-center gap-2 overflow-hidden ${isUrdu ? 'font-urdu flex-row-reverse' : ''}`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
                <svg className={`w-5 h-5 relative z-10 transform group-hover:scale-110 transition-transform duration-300 ${isUrdu ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="relative z-10">{isUrdu ? 'ڈیش بورڈ' : 'Dashboard'}</span>
              </button>

              {/* Page Heading - Right side in Urdu, Left side in English */}
              <div className={`flex items-center gap-4 ${isUrdu ? 'flex-row-reverse' : ''}`}>
                <div className="relative">
                  <div className="absolute inset-0 bg-purple-400 rounded-xl blur-lg opacity-30 animate-pulse"></div>
                  <div className="relative bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl p-3 shadow-lg">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div className={isUrdu ? 'text-right' : 'text-left'}>
                  <h1 className={`text-3xl font-bold bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif", lineHeight: '1.6' } : { lineHeight: '1.6' }}>
                    {isUrdu ? 'اخراجات کی تفصیلات' : 'Expense Details'}
                  </h1>
                  <p className={`text-gray-600 text-sm font-medium mt-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'ہر اخراجات فیلڈ کی الگ الگ تفصیلات' : 'Separate details for each expense field'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className={`mt-4 text-gray-600 ${isUrdu ? 'font-urdu' : ''}`}>
                {isUrdu ? 'ڈیٹا لوڈ ہو رہا ہے...' : 'Loading data...'}
              </p>
            </div>
          ) : (
            <div>
              {/* Filter + Tabs */}
              <div className="mb-4">
                {/* Filter Section (match Bills compact style) */}
                <div
                  className="mb-3 p-1.5 bg-gradient-to-r from-green-50 to-lime-50 rounded-xl border border-green-200 animate-fade-in-down shadow-lg print:hidden"
                  dir={isUrdu ? 'rtl' : 'ltr'}
                  data-export-hide="true"
                >
                  <div className="flex justify-between items-center mb-2">
                    <h2 className={`text-base font-bold text-green-700 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'فلٹرز' : 'Filters'}
                    </h2>
                    <div className={`flex gap-2 flex-wrap ${isUrdu ? 'flex-row-reverse' : ''}`}>
                      <button
                        onClick={handleDownloadExcel}
                        className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-all print:hidden"
                        type="button"
                      >
                        Excel
                      </button>
                      <button
                        onClick={handlePrint}
                        className="px-2 py-1 bg-gray-700 hover:bg-gray-800 text-white text-xs rounded-lg transition-all print:hidden"
                        type="button"
                      >
                        {isUrdu ? 'پرنٹ' : 'Print'}
                      </button>
                      <button
                        onClick={handleDownloadPDF}
                        disabled={isGeneratingPDF}
                        className={`px-2 py-1 text-white text-xs rounded-lg transition-all print:hidden ${isGeneratingPDF ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                          }`}
                        type="button"
                      >
                        {isGeneratingPDF ? (isUrdu ? 'PDF...' : 'PDF...') : 'PDF'}
                      </button>
                      <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`px-3 py-1 rounded-lg transition-all flex items-center gap-2 ${showFilters || hasActiveFilters
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                          }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                        {isUrdu ? 'فلٹر' : 'Filter'}
                        {hasActiveFilters && (
                          <span className="bg-white text-green-600 rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold">
                            !
                          </span>
                        )}
                      </button>
                      {hasActiveFilters && (
                        <button
                          onClick={resetFilters}
                          className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white text-xs rounded-lg transition-all"
                        >
                          {isUrdu ? 'ری سیٹ' : 'Reset'}
                        </button>
                      )}
                      {showFilters && (
                        <button
                          onClick={() => setShowFilters(false)}
                          className="px-2 py-1 bg-gray-300 hover:bg-gray-400 text-gray-700 text-xs rounded-lg transition-all"
                        >
                          {isUrdu ? 'بند کریں' : 'Close'}
                        </button>
                      )}
                    </div>
                  </div>

                  {showFilters && (
                    <div className="pt-2 border-t border-green-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1.5">
                        {/* Search Input */}
                        <div>
                          <label className={`block text-xs font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                            {isUrdu ? 'تلاش:' : 'Search:'}
                          </label>
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={isUrdu ? 'بل نمبر، سپلائر...' : 'Bill Number, Supplier...'}
                            className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
                          />
                        </div>

                        {/* Date From */}
                        <div>
                          <label className={`block text-xs font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                            {isUrdu ? 'تاریخ سے:' : 'Date From:'}
                          </label>
                          <input
                            type="date"
                            value={filterDateFrom}
                            onChange={(e) => setFilterDateFrom(e.target.value)}
                            className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
                          />
                        </div>

                        {/* Date To */}
                        <div>
                          <label className={`block text-xs font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                            {isUrdu ? 'تاریخ تک:' : 'Date To:'}
                          </label>
                          <input
                            type="date"
                            value={filterDateTo}
                            onChange={(e) => setFilterDateTo(e.target.value)}
                            className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
                          />
                        </div>

                        {/* Supplier Filter */}
                        <div>
                          <label className={`block text-xs font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                            {isUrdu ? 'سپلائر:' : 'Supplier:'}
                          </label>
                          <select
                            value={filterSupplier}
                            onChange={(e) => setFilterSupplier(e.target.value)}
                            className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
                          >
                            <option value="">{isUrdu ? 'تمام' : 'All'}</option>
                            {uniqueSuppliers.map(supplier => (
                              <option key={supplier} value={supplier}>{supplier}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Tabs Navigation */}
                <div className="bg-white rounded-xl shadow-lg overflow-x-auto" dir={isUrdu ? 'rtl' : 'ltr'}>
                  <div className="flex border-b border-gray-200">
                    <button
                      onClick={() => setActiveTab('cash')}
                      className={`px-6 py-4 font-medium text-sm transition-all whitespace-nowrap border-b-2 ${activeTab === 'cash'
                          ? 'border-purple-600 text-purple-600 font-semibold'
                          : 'border-transparent text-gray-500 hover:text-purple-600 hover:border-purple-300'
                        } ${isUrdu ? 'font-urdu' : ''}`}
                    >
                      {isUrdu ? 'نقدی' : 'Cash'}
                      <span className={`ml-2 text-xs ${isUrdu ? 'mr-2 ml-0' : ''}`}>
                        ({filteredExpenseFieldsData.cash.length}/{expenseFieldsData.cash.length})
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveTab('rent')}
                      className={`px-6 py-4 font-medium text-sm transition-all whitespace-nowrap border-b-2 ${activeTab === 'rent'
                          ? 'border-purple-600 text-purple-600 font-semibold'
                          : 'border-transparent text-gray-500 hover:text-purple-600 hover:border-purple-300'
                        } ${isUrdu ? 'font-urdu' : ''}`}
                    >
                      {isUrdu ? 'کرایه' : 'Rent'}
                      <span className={`ml-2 text-xs ${isUrdu ? 'mr-2 ml-0' : ''}`}>
                        ({filteredExpenseFieldsData.rent.length}/{expenseFieldsData.rent.length})
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveTab('commission')}
                      className={`px-6 py-4 font-medium text-sm transition-all whitespace-nowrap border-b-2 ${activeTab === 'commission'
                          ? 'border-purple-600 text-purple-600 font-semibold'
                          : 'border-transparent text-gray-500 hover:text-purple-600 hover:border-purple-300'
                        } ${isUrdu ? 'font-urdu' : ''}`}
                    >
                      {isUrdu ? 'کمیشن' : 'Commission'}
                      <span className={`ml-2 text-xs ${isUrdu ? 'mr-2 ml-0' : ''}`}>
                        ({filteredExpenseFieldsData.commission.length}/{expenseFieldsData.commission.length})
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveTab('labor')}
                      className={`px-6 py-4 font-medium text-sm transition-all whitespace-nowrap border-b-2 ${activeTab === 'labor'
                          ? 'border-purple-600 text-purple-600 font-semibold'
                          : 'border-transparent text-gray-500 hover:text-purple-600 hover:border-purple-300'
                        } ${isUrdu ? 'font-urdu' : ''}`}
                    >
                      {isUrdu ? 'مزدوری' : 'Labor'}
                      <span className={`ml-2 text-xs ${isUrdu ? 'mr-2 ml-0' : ''}`}>
                        ({filteredExpenseFieldsData.labor.length}/{expenseFieldsData.labor.length})
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveTab('mashiana')}
                      className={`px-6 py-4 font-medium text-sm transition-all whitespace-nowrap border-b-2 ${activeTab === 'mashiana'
                          ? 'border-purple-600 text-purple-600 font-semibold'
                          : 'border-transparent text-gray-500 hover:text-purple-600 hover:border-purple-300'
                        } ${isUrdu ? 'font-urdu' : ''}`}
                    >
                      {isUrdu ? 'مشیانه' : 'Mashiana'}
                      <span className={`ml-2 text-xs ${isUrdu ? 'mr-2 ml-0' : ''}`}>
                        ({filteredExpenseFieldsData.mashiana.length}/{expenseFieldsData.mashiana.length})
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveTab('marketFees')}
                      className={`px-6 py-4 font-medium text-sm transition-all whitespace-nowrap border-b-2 ${activeTab === 'marketFees'
                          ? 'border-purple-600 text-purple-600 font-semibold'
                          : 'border-transparent text-gray-500 hover:text-purple-600 hover:border-purple-300'
                        } ${isUrdu ? 'font-urdu' : ''}`}
                    >
                      {isUrdu ? 'مارکیٹ فیس' : 'Market Fees'}
                      <span className={`ml-2 text-xs ${isUrdu ? 'mr-2 ml-0' : ''}`}>
                        ({filteredExpenseFieldsData.marketFees.length}/{expenseFieldsData.marketFees.length})
                      </span>
                    </button>
                    <button
                      onClick={() => setActiveTab('dailyKharcha')}
                      className={`px-6 py-4 font-medium text-sm transition-all whitespace-nowrap border-b-2 ${activeTab === 'dailyKharcha'
                          ? 'border-purple-600 text-purple-600 font-semibold'
                          : 'border-transparent text-gray-500 hover:text-purple-600 hover:border-purple-300'
                        } ${isUrdu ? 'font-urdu' : ''}`}
                    >
                      {isUrdu ? 'روزانه خرچہ' : 'Daily Kharcha'}
                      <span className={`ml-2 text-xs ${isUrdu ? 'mr-2 ml-0' : ''}`}>
                        ({dailyKharchas.length})
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Active Tab Content */}
              <div ref={printRef} id="expense-details-print-area">
                <div className="hidden print:block border-b border-gray-300 pb-2 mb-3 bg-white">
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <h2 className={`text-lg font-bold ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'اخراجات کی تفصیلات' : 'Expense Fields Details'}
                      </h2>
                      <p className="text-xs text-gray-600">
                        {activeTabTitle}
                      </p>
                    </div>
                    <div className="text-xs text-gray-600 text-right">
                      <div>
                        {isUrdu ? 'تاریخ:' : 'Date:'} {new Date().toLocaleDateString()}
                      </div>
                      <div>
                        {isUrdu ? 'ٹوٹل:' : 'Total:'} {activeTabData.length}
                      </div>
                    </div>
                  </div>
                </div>
                {activeTab === 'cash' && renderFieldTable('cash', 'Cash', 'نقدی', filteredExpenseFieldsData.cash)}
                {activeTab === 'rent' && renderFieldTable('rent', 'Rent', 'کرایه', filteredExpenseFieldsData.rent)}
                {activeTab === 'commission' && renderFieldTable('commission', 'Commission', 'کمیشن', filteredExpenseFieldsData.commission)}
                {activeTab === 'labor' && renderFieldTable('labor', 'Labor', 'مزدوری', filteredExpenseFieldsData.labor)}
                {activeTab === 'mashiana' && renderFieldTable('mashiana', 'Mashiana', 'مشیانه', filteredExpenseFieldsData.mashiana)}
                {activeTab === 'marketFees' && renderFieldTable('marketFees', 'Market Fees', 'مارکیٹ فیس', filteredExpenseFieldsData.marketFees)}
                {activeTab === 'dailyKharcha' && (
                  <div className="bg-white rounded-xl shadow-lg p-6" dir={isUrdu ? 'rtl' : 'ltr'}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Form Section */}
                      <div>
                        <h3 className={`text-lg font-bold mb-4 text-purple-800 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                          {isUrdu ? 'روزانه خرچہ شامل کریں' : 'Add Daily Kharcha'}
                        </h3>
                        <form onSubmit={handleDailyKharchaSubmit} className="space-y-4">
                          <div>
                            <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                              {isUrdu ? 'تاریخ:' : 'Date:'}
                            </label>
                            <input
                              type="date"
                              value={dailyKharchaForm.date}
                              onChange={(e) => setDailyKharchaForm({ ...dailyKharchaForm, date: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-1 focus:ring-purple-200"
                              required
                            />
                          </div>
                          <div>
                            <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                              {isUrdu ? 'رقم:' : 'Amount:'}
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={dailyKharchaForm.amount}
                              onChange={(e) => setDailyKharchaForm({ ...dailyKharchaForm, amount: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-1 focus:ring-purple-200"
                              placeholder={isUrdu ? 'رقم درج کریں' : 'Enter amount'}
                              required
                            />
                          </div>
                          <div>
                            <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                              {isUrdu ? 'تفصیل:' : 'Description:'}
                            </label>
                            <textarea
                              value={isUrdu ? (dailyKharchaForm.tafseelUrdu || dailyKharchaForm.tafseel) : dailyKharchaForm.tafseel}
                              onChange={(e) => {
                                if (isUrdu) {
                                  setDailyKharchaForm({ ...dailyKharchaForm, tafseelUrdu: e.target.value });
                                } else {
                                  setDailyKharchaForm({ ...dailyKharchaForm, tafseel: e.target.value });
                                }
                              }}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-1 focus:ring-purple-200 ${isUrdu ? 'font-urdu' : ''}`}
                              placeholder={isUrdu ? 'تفصیل درج کریں' : 'Enter description'}
                              rows="3"
                            />
                          </div>
                          <div>
                            <label className={`block text-sm font-medium text-gray-700 mb-1 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                              {isUrdu ? 'ادائیگی کا طریقہ:' : 'Payment Method:'}
                            </label>
                            <select
                              value={dailyKharchaForm.paymentMethod}
                              onChange={(e) => setDailyKharchaForm({ ...dailyKharchaForm, paymentMethod: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-1 focus:ring-purple-200"
                            >
                              <option value="">{isUrdu ? 'منتخب کریں' : 'Select payment method'}</option>
                              {paymentMethods.map((method) => (
                                <option key={method._id} value={method.nameUrdu || method.name}>
                                  {isUrdu ? (method.nameUrdu || method.name) : (method.name || method.nameUrdu)}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex gap-2" style={isUrdu ? { flexDirection: 'row-reverse' } : {}}>
                            {editingDailyKharcha && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingDailyKharcha(null);
                                  setDailyKharchaForm({
                                    date: new Date().toISOString().split('T')[0],
                                    amount: '',
                                    tafseel: '',
                                    tafseelUrdu: '',
                                    paymentMethod: ''
                                  });
                                }}
                                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                              >
                                {isUrdu ? 'منسوخ کریں' : 'Cancel'}
                              </button>
                            )}
                            <button
                              type="submit"
                              disabled={isSavingDailyKharcha}
                              className={`px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors ${isSavingDailyKharcha ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {isSavingDailyKharcha
                                ? (isUrdu ? 'محفوظ ہو رہا ہے...' : 'Saving...')
                                : (editingDailyKharcha ? (isUrdu ? 'تبدیل کریں' : 'Update') : (isUrdu ? 'محفوظ کریں' : 'Save'))
                              }
                            </button>
                          </div>
                        </form>
                      </div>

                      {/* List Section */}
                      <div>
                        <h3 className={`text-lg font-bold mb-4 text-purple-800 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                          {isUrdu ? 'روزانه خرچہ کی فہرست' : 'Daily Kharcha List'}
                        </h3>
                        {dailyKharchas.length > 0 ? (
                          <div className="overflow-auto max-h-[500px] rounded-lg border border-gray-300 bg-white">
                            <table className="min-w-full" dir={isUrdu ? 'rtl' : 'ltr'}>
                              <thead className="bg-gradient-to-r from-purple-100 to-pink-100">
                                <tr>
                                  <th className={`px-4 py-3 text-xs font-bold text-gray-700 border-b ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                                    {isUrdu ? 'تاریخ' : 'Date'}
                                  </th>
                                  <th className={`px-4 py-3 text-xs font-bold text-gray-700 border-b ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                                    {isUrdu ? 'رقم' : 'Amount'}
                                  </th>
                                  <th className={`px-4 py-3 text-xs font-bold text-gray-700 border-b ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                                    {isUrdu ? 'تفصیل' : 'Description'}
                                  </th>
                                  <th className={`px-4 py-3 text-xs font-bold text-gray-700 border-b ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                                    {isUrdu ? 'ادائیگی کا طریقہ' : 'Payment Method'}
                                  </th>
                                  <th className={`px-4 py-3 text-xs font-bold text-gray-700 border-b ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                                    {isUrdu ? 'عمل' : 'Actions'}
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {dailyKharchas.map((item) => (
                                  <tr key={item._id} className="hover:bg-gray-50">
                                    <td className={`px-4 py-3 text-sm text-gray-800 ${isUrdu ? 'text-right' : 'text-left'}`}>
                                      {item.date ? new Date(item.date).toISOString().split('T')[0] : '-'}
                                    </td>
                                    <td className={`px-4 py-3 text-sm text-gray-800 ${isUrdu ? 'text-right' : 'text-left'}`}>
                                      {(item.amount || 0).toFixed(2)}
                                    </td>
                                    <td className={`px-4 py-3 text-sm text-gray-800 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                                      {isUrdu ? (item.tafseelUrdu || item.tafseel || '-') : (item.tafseel || item.tafseelUrdu || '-')}
                                    </td>
                                    <td className={`px-4 py-3 text-sm text-gray-800 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                                      {item.paymentMethod || '-'}
                                    </td>
                                    <td className={`px-4 py-3 text-sm ${isUrdu ? 'text-right' : 'text-left'}`}>
                                      <div className="flex gap-2" style={isUrdu ? { flexDirection: 'row-reverse' } : {}}>
                                        <button
                                          onClick={() => handleEditDailyKharcha(item)}
                                          className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded transition-colors"
                                        >
                                          {isUrdu ? 'تبدیل' : 'Edit'}
                                        </button>
                                        <button
                                          onClick={() => handleDeleteDailyKharcha(item._id)}
                                          className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded transition-colors"
                                        >
                                          {isUrdu ? 'حذف' : 'Delete'}
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-gray-50">
                                <tr>
                                  <td colSpan="1" className={`px-4 py-3 text-sm font-bold text-gray-700 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                                    {isUrdu ? 'کل' : 'Total'}
                                  </td>
                                  <td className={`px-4 py-3 text-sm font-bold text-gray-700 ${isUrdu ? 'text-right' : 'text-left'}`}>
                                    {dailyKharchas.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)}
                                  </td>
                                  <td colSpan="3"></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        ) : (
                          <div className={`text-center py-8 text-gray-500 ${isUrdu ? 'font-urdu' : ''}`}>
                            {isUrdu ? 'کوئی ڈیٹا نہیں ملا' : 'No data found'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`
        @media print {
          /* Print only the report area (avoid dashboard header/tabs messing up the top) */
          body * {
            visibility: hidden !important;
          }
          #expense-details-print-area,
          #expense-details-print-area * {
            visibility: visible !important;
          }
          #expense-details-print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            padding: 0.5cm !important;
          }
          body {
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          @page {
            margin: 0.5cm;
          }

          /* Force-hide filter + footer + any other export-only controls */
          [data-export-hide="true"] {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
}

export default ExpenseFieldsDetails;

